/**
 * Persist the editor's .docx blob, reporting honestly whether it landed.
 *
 * Split out of `runSave` for MDRS-545 / audit RB-10, because this is where the
 * save-success boolean is actually made — and finalise now refuses to freeze
 * and lock the case unless that boolean is true.
 *
 * The trap this exists to close: `DocxEditorRef.save()` is typed
 * `Promise<ArrayBuffer | null>` and returns null both when the editor isn't
 * mounted (it is lazy-loaded behind Suspense while the Finalise button is
 * already live) and when the serializer throws internally. The obvious
 * `if (docx) { upload }` treats both as "nothing to do" and falls through as
 * success — so a save that persisted NOTHING was indistinguishable from one
 * that persisted everything, and finalise would go on to freeze the previous
 * document.
 *
 * The invariant callers rely on: `ok === true` means the blob was uploaded.
 */
export type PersistDocxResult =
  | { ok: true }
  | {
      ok: false;
      /**
       * `editor-not-ready`  no buffer and no error: <DocxEditor> is still
       *                     lazy-loading behind Suspense. TRANSIENT — waiting
       *                     genuinely helps.
       * `serialise-failed`  the serializer threw. NOT transient — telling the
       *                     doctor to wait would loop them forever on an
       *                     action they need to complete.
       * `upload-failed`     a buffer was produced but the PUT failed, e.g. the
       *                     ingress CORS policy that dropped images on dev.
       *
       * The first two were one reason until review pointed out they need
       * opposite advice — and the distinction already existed here, it was
       * just being discarded.
       */
      reason: "editor-not-ready" | "serialise-failed" | "upload-failed";
      error?: unknown;
    };

export const persistEditorDocx = async (
  save: () => Promise<ArrayBuffer | null> | undefined,
  upload: (docx: ArrayBuffer) => Promise<unknown>
): Promise<PersistDocxResult> => {
  let docx: ArrayBuffer | null | undefined;
  try {
    docx = await save();
  } catch (error) {
    return { ok: false, reason: "serialise-failed", error };
  }

  // Zero-length is not a document. `new ArrayBuffer(0)` is truthy, so without
  // the byteLength check an empty serialisation would upload and report ok —
  // and the file header states the invariant absolutely.
  if (!docx || docx.byteLength === 0) {
    return { ok: false, reason: "editor-not-ready" };
  }

  try {
    await upload(docx);
  } catch (error) {
    return { ok: false, reason: "upload-failed", error };
  }

  return { ok: true };
};

/** Why a save didn't fully land. Carried to the caller so whoever reports the
 *  failure can name the cause — the notification slice holds ONE message, so a
 *  caller that adds consequence must also carry the cause or it destroys it. */
export type SaveFailureReason =
  | PersistDocxFailure["reason"]
  | "no-case"
  // MDRS-554: the server refused the write because another tab or user saved
  // first. Distinct from every reason above because nothing is wrong with
  // this client — retrying is futile and the user has a decision to make.
  | "conflict";

type PersistDocxFailure = Extract<PersistDocxResult, { ok: false }>;

/** Result of an explicit save. `ok` is true only when the ENTIRE editor state
 *  persisted — draft payload AND the .docx blob. */
export type SaveOutcome = { ok: true } | { ok: false; reason: SaveFailureReason };

/** User-facing cause, phrased for someone who just lost work. Kept next to the
 *  reasons so a new reason can't be added without deciding what to say. */
export const saveFailureMessage = (reason: SaveFailureReason): string => {
  switch (reason) {
    case "upload-failed":
      return "Your latest changes (including any images) couldn't be saved. Check your connection.";
    case "editor-not-ready":
      return "The editor hadn't finished loading, so your latest changes couldn't be saved. Give it a moment and try again.";
    case "serialise-failed":
      return "Your latest changes couldn't be prepared for saving. If this keeps happening, contact support — retrying won't clear it.";
    case "no-case":
      return "There's no active report to save.";
    case "conflict":
      // Deliberately terse: the conflict dialog is what explains this, and the
      // notification slice holds exactly one message. A caller that gates on
      // the save (finalise) needs a sentence that doesn't contradict it.
      return "This report was changed somewhere else, so your latest changes weren't saved.";
    default:
      // A malformed reason must not render "undefined …" into the one message
      // slot. Falling off the switch returned exactly that.
      return "Your latest changes couldn't be saved.";
  }
};
